import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SubscribedUserService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the query.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching the subscribed user list.
 *
 * @param {Object} params - Query parameters (page, limit, search, plan_id, user_type, status,
 * purchase_date_from, purchase_date_to, sort_by, order_by)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useSubscribedUserList = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["subscribed-user-list", params],
    queryFn: () => {
      return SubscribedUserService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single subscribed user's subscription details.
 *
 * @param {string} uuid - Subscription UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useSubscribedUser = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["subscribed-user", uuid],
    queryFn: () => {
      return SubscribedUserService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for fetching the renewal/transaction history of a subscription.
 *
 * The query is lazy-gated so the caller can defer fetching until, e.g., the
 * "Renewal & Transaction History" tab is actually selected.
 *
 * @param {string} uuid - Subscription UUID
 * @param {Object} params - Query parameters (page, limit)
 * @param {Object} [options] - Extra options.
 * @param {boolean} [options.enabled=true] - Caller-controlled gate, ANDed with `!!uuid`.
 * @param {function} [options.onSuccess] - Callback to execute when the query is successful.
 * @param {function} [options.onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useSubscribedUserTransactionList = (uuid, params = {}, options = {}) => {
  const { enabled = true, onSuccess, onError = defaultError } = options;

  return useQuery({
    queryKey: ["subscribed-user-transactions", uuid, params],
    queryFn: () => {
      return SubscribedUserService.getTransactions(uuid, params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid && enabled,
  });
};

/**
 * Custom hook for fetching the subscription owner's uploaded verification documents (WEAL-466).
 *
 * The query is lazy-gated so the caller can defer fetching until, e.g., the "Documents" /
 * verification tab is actually selected.
 *
 * @param {string} uuid - Subscription UUID
 * @param {Object} [options] - Extra options.
 * @param {boolean} [options.enabled=true] - Caller-controlled gate, ANDed with `!!uuid`.
 * @param {function} [options.onSuccess] - Callback to execute when the query is successful.
 * @param {function} [options.onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useSubscribedUserDocuments = (uuid, options = {}) => {
  const { enabled = true, onSuccess, onError = defaultError } = options;

  return useQuery({
    queryKey: ["subscribed-user-documents", uuid],
    queryFn: () => {
      return SubscribedUserService.getDocuments(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid && enabled,
  });
};

/**
 * Custom hook for approving the subscription owner's profile verification (WEAL-466).
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useApproveVerification = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return SubscribedUserService.approveVerification(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["subscribed-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscribed-user", uuid] });
      queryClient.invalidateQueries({ queryKey: ["subscribed-user-documents", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for rejecting the subscription owner's profile verification with a reason (WEAL-466).
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useRejectVerification = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, reason }) => {
      return SubscribedUserService.rejectVerification(uuid, reason);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subscribed-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscribed-user", variables?.uuid] });
      queryClient.invalidateQueries({ queryKey: ["subscribed-user-documents", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useSubscribedUserList,
  useSubscribedUser,
  useSubscribedUserTransactionList,
  useSubscribedUserDocuments,
  useApproveVerification,
  useRejectVerification,
};
